// ==============================
// TablesTabsView.tsx
// ==============================
import React, { useState } from "react";
import { Box, Tabs, Tab, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import { User, Workout } from "../../../constants/types";

interface TablesTabsViewProps {
  users: User[];
  workouts: Workout[];
}

export default function TablesTabsView({
  users,
  workouts,
}: TablesTabsViewProps) {
  const [tableTabIndex, setTableTabIndex] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTableTabIndex(newValue);
  };

  // ---------------------
  // Users Table
  // ---------------------
  const userRows = users.map((u) => ({
    ...u,
    id: u.userId,
    trainingGoal: u.goals?.trainingGoal ?? "",
  }));

  const userColumns: GridColDef[] = [
    { field: "userId", headerName: "User ID", width: 150 },
    { field: "name", headerName: "Name", width: 150 },
    { field: "email", headerName: "Email", width: 200 },
    { field: "trainingGoal", headerName: "Training Goal", width: 150 },
    { field: "activityLevel", headerName: "Activity Level", width: 150 },
    { field: "gender", headerName: "Gender", width: 100 },
    { field: "height", headerName: "Height (cm)", width: 100 },
    { field: "weight", headerName: "Weight", width: 100 },
    // You can add more columns if desired (created, updated, etc.)
    { field: "created", headerName: "Created", width: 150 },
    { field: "updated", headerName: "Updated", width: 150 },
  ];

  // ---------------------
  // Workouts Table
  // ---------------------
  const workoutRows = workouts.map((w) => ({
    ...w,
    id: w.id,
    templateTitle: w.template?.title ?? "",
  }));

  const workoutColumns: GridColDef[] = [
    { field: "id", headerName: "Workout ID", width: 150 },
    { field: "userId", headerName: "User ID", width: 150 },
    { field: "dayOfExecution", headerName: "Date", width: 150 },
    { field: "durationInMinutes", headerName: "Duration (min)", width: 150 },
    { field: "totalVolume", headerName: "Total Volume", width: 150 },
    { field: "templateTitle", headerName: "Template Title", width: 200 },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <Tabs value={tableTabIndex} onChange={handleTabChange} centered>
        <Tab label="Users" />
        <Tab label="Workouts" />
      </Tabs>

      {/* Tab Panel 1: Users Table */}
      {tableTabIndex === 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h5" gutterBottom>
            Users
          </Typography>
          <Box sx={{ height: 500 }}>
            <DataGrid
              rows={userRows}
              columns={userColumns}
              disableRowSelectionOnClick
              slots={{
                toolbar: GridToolbar,
              }}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                },
              }}
              initialState={{
                pagination: { paginationModel: { pageSize: 5, page: 0 } },
              }}
              pageSizeOptions={[5, 10, 20]}
            />
          </Box>
        </Box>
      )}

      {/* Tab Panel 2: Workouts Table */}
      {tableTabIndex === 1 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h5" gutterBottom>
            Workouts
          </Typography>
          <Box sx={{ height: 500 }}>
            <DataGrid
              rows={workoutRows}
              columns={workoutColumns}
              disableRowSelectionOnClick
              slots={{
                toolbar: GridToolbar,
              }}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                },
              }}
              initialState={{
                pagination: { paginationModel: { pageSize: 5, page: 0 } },
              }}
              pageSizeOptions={[5, 10, 20]}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}
